import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const BULLETS = [
  "Live 1-on-1 over Zoom",
  "Personalised to your goals",
  "Warm, judgment-free space",
];

export default function BookForm({ areaOptions = [] }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "",
    area_of_interest: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/inquiries", { ...form, intention: form.area_of_interest });
      setDone(true);
      toast.success("Your request is in. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally { setBusy(false); }
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="book" className="py-16 md:py-24" style={{ background: "var(--ev-surface)" }}>
      <div className="mx-auto max-w-[1200px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <div className="eyebrow">Book your free session</div>
          <h2 className="font-serif-ev mt-4 text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
            Your first session is on us.
          </h2>
          <p className="mt-6 text-[15px] md:text-base max-w-md" style={{ color: "var(--ev-muted)" }}>
            Share where you are and what you're longing for. No credit card required — just the first gentle
            step toward a life that feels aligned.
          </p>
          <ul className="mt-8 space-y-3">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-center gap-3 text-[15px]" style={{ color: "var(--ev-ink-2)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "var(--ev-blue-2)" }} />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-7">
          {done ? (
            <div className="ev-card-cream p-8 md:p-10">
              <div className="w-14 h-14 rounded-full grid place-items-center mb-4" style={{ background: "var(--ev-blue-2)" }}>
                <Check className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-serif-ev text-3xl md:text-4xl leading-tight">You're in.</h3>
              <p className="mt-3 text-[15px] md:text-base" style={{ color: "var(--ev-muted)" }}>
                Prriyanka reviews every request personally. You'll hear from us within 24 hours to confirm your first free session.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="ev-card-cream p-6 md:p-10 space-y-5">
              <div className="text-xs" style={{ color: "#B4553F" }}>* indicates a required field</div>

              <Field label="Full name *" required>
                <input data-testid={HOME.inquiryName} required value={form.name} onChange={setField("name")}
                       placeholder="Your name" className={inp} />
              </Field>

              <Field label="Phone *" required>
                <input data-testid={HOME.inquiryPhone} required value={form.phone} onChange={setField("phone")}
                       placeholder="Your phone number" className={inp} />
              </Field>

              <Field label="Email *" required>
                <input data-testid={HOME.inquiryEmail} required type="email" value={form.email} onChange={setField("email")}
                       placeholder="you@email.com" className={inp} />
              </Field>

              <Field label="Location *" required>
                <input data-testid="inquiry-location" required value={form.location} onChange={setField("location")}
                       placeholder="City, Country" className={inp} />
              </Field>

              <Field label="Area of Interest">
                <select data-testid={HOME.inquiryIntention} value={form.area_of_interest} onChange={setField("area_of_interest")} className={inp + " appearance-none"}>
                  <option value="">Select a focus</option>
                  {areaOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                </select>
              </Field>

              <Field label="Your message">
                <textarea rows={4} value={form.message} onChange={setField("message")}
                          placeholder="Tell me a little about what's bringing you here..."
                          className="w-full bg-white border hairline rounded-2xl py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]" />
              </Field>

              <button data-testid={HOME.inquirySubmit} type="submit" disabled={busy}
                      className="ev-btn ev-btn-primary w-full disabled:opacity-60">
                {busy ? "Sending…" : "Book my free session"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const inp = "w-full bg-white border hairline rounded-full py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
